import { useState } from 'react';
import { templates, AssistantTemplate } from '../data/templates';

interface Props {
  onSelectTemplate: (templateId: string) => void;
}

type Category = 'all' | 'clinical' | 'compliance' | 'operations' | 'communication';

const CATEGORY_PILLS: { label: string; value: Category }[] = [
  { label: 'All', value: 'all' },
  { label: 'Clinical', value: 'clinical' },
  { label: 'Compliance', value: 'compliance' },
  { label: 'Operations', value: 'operations' },
  { label: 'Communication', value: 'communication' },
];

const CATEGORY_BADGE_COLORS: Record<AssistantTemplate['category'], string> = {
  clinical: 'bg-blue-100 text-blue-700',
  compliance: 'bg-amber-100 text-amber-700',
  operations: 'bg-purple-100 text-purple-700',
  communication: 'bg-pink-100 text-pink-700',
};

export function TemplateLibrary({ onSelectTemplate }: Props) {
  const [activeCategory, setActiveCategory] = useState<Category>('all');

  const filtered = activeCategory === 'all'
    ? templates
    : templates.filter(t => t.category === activeCategory);

  return (
    <div>
      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORY_PILLS.map(pill => (
          <button
            key={pill.value}
            onClick={() => setActiveCategory(pill.value)}
            className={`px-4 py-1.5 rounded-pill text-sm font-medium transition-colors ${
              activeCategory === pill.value
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            {pill.label}
          </button>
        ))}
      </div>

      {/* Card grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(template => (
          <div key={template.id} className="card p-5 flex flex-col gap-3">
            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_BADGE_COLORS[template.category]}`}>
                {CATEGORY_PILLS.find(p => p.value === template.category)?.label ?? template.category}
              </span>
              {template.isCommunity && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                  Community
                </span>
              )}
            </div>

            {/* Name */}
            <h3 className="text-sm font-semibold text-neutral-800">{template.name}</h3>

            {/* Description */}
            <p className="text-xs text-neutral-500">{template.description}</p>

            {/* Use cases */}
            <div className="text-xs text-neutral-500">
              <span className="font-medium text-neutral-600">Use cases: </span>
              {template.suggestedStarters.join(' · ')}
            </div>

            {/* Action */}
            <div className="mt-auto pt-2 border-t border-neutral-100">
              <button
                onClick={() => onSelectTemplate(template.id)}
                className="btn-primary text-xs w-full"
              >
                Use Template
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
